import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import ChatList from "@/features/chat-list/ChatList.vue";
import { ComputedRef, inject, onMounted, onUnmounted, ref, watch } from "vue";
import SplashScreen from "@/features/splash-screen/SplashScreen.vue";
import MinimizedButton from "@/features/minimized-button/MinimizedButton.vue";
import { setExpandedState, setMinimizedState } from "@/utils/iframe-settings";
import activeWidgetPersistence from "@/utils/activeWidgetPersistence";
import { ActiveWidget, TenantSettings } from "@/utils/models";
import PreChatSurvey from "@/features/pre-chat-survey/PreChatSurvey.vue";
import { useContactPersistence } from "@/utils/contactPersistence";
import { useActiveConversationPersistence } from "@/utils/activeConversationPersistence";
import { useConversationEventListener } from "@/features/chat-list/useConversationEventListener";
import { useSubscribe } from "@/composables/useSubscribe";
import {
  GOnWebChatConversationTimelineCreatedSubscription,
  GOnWebChatConversationTimelineCreatedSubscriptionVariables,
  OnWebChatConversationTimelineCreatedDocument,
} from "@/generated/graphql";
import emitter from "@/utils/mitt";
import { useEmitterOn } from "@/composables/useEmitterOn";
import { getConversationTimelineContent } from "@/utils/conversationTimelineDataHelper";
import { debounce } from "@/utils/debounce";
import conversationHistoryPersistence, { ConversationHistory } from "@/utils/conversationHistoryPersistence";
import { useGraphqlSdk } from "@/graphql/graphql-client";
import { checkAndPersistVisitorCountryCode } from "./utils/visitorCountryHelper";
import { settingsInjectionKey, tenantInjectionKey, demoModeInjectionKey, connectedServiceInjectionKey } from "./keys";
import { resolveDisplayName, resolveNameInitials } from "./utils/display-name-helper";


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const activeWidget = ref<ActiveWidget>(ActiveWidget.Splash);
const previousActiveWidget = ref<ActiveWidget | undefined>(undefined);

const loading = ref(false);
const hasSubscribed = ref(false);

const settings = inject(settingsInjectionKey) as ComputedRef<TenantSettings>;
const tenant = inject(tenantInjectionKey);
const demoMode = inject(demoModeInjectionKey);
const connectedService = inject(connectedServiceInjectionKey);
// contact.value = contactPersistence.getState();

const activeConversationPersistence = useActiveConversationPersistence(connectedService ?? "demo");

activeWidget.value = activeWidgetPersistence.getState() ?? ActiveWidget.Minimized;

const contactPersistence = useContactPersistence(tenant);

useConversationEventListener();
subscribe();

function onClose() {
  const el = document.getElementById("sa-chat-widget-iframe") as HTMLIFrameElement;
  if (el) {
    previousActiveWidget.value = activeWidget.value;
    activeWidget.value = ActiveWidget.Minimized;
  } else {
    console.warn("Could not find element by id 'sa-chat-widget-iframe'");
  }
}

function onBack() {
  activeWidget.value = ActiveWidget.Splash;
}

function onConversationClick(conversation: string) {
  activeConversationPersistence.saveState(conversation);
  activeWidget.value = ActiveWidget.Conversation;
}

function onStartConversation() {
  activeConversationPersistence.saveState("");

  // We need information
  if (settings.value.preChatSurveyFields?.length) {
    activeWidget.value = ActiveWidget.PreChatSurvey;
  }

  // We don't need contact information - go to conversation
  else {
    activeWidget.value = ActiveWidget.Conversation;
  }
}

const fetchConversationData = async () => {
  try {
    loading.value = true;
    const contact = contactPersistence.getState();

    if (!tenant || demoMode) {
      const demoHistory: ConversationHistory[] = [];
      console.log("returning demo history");
      return demoHistory;
    }

    if (!contact) {
      onStartConversation();
      return null;
    }

    const res = await useGraphqlSdk().GetConversations({
      contact: contact,
      tenant: tenant,
      take: 30,
    });

    const history = res.conversations.map<ConversationHistory>(i => {
      const temp: ConversationHistory = {
        conversation: i.id,
        content: i.lastMessageContent ?? "",
        date: Date.parse(i.updated),
        ended: false,
      };
      return temp;
    });

    conversationHistoryPersistence.saveState(history);

    return history;
  } catch (err) {
    console.warn("Error getting conversations", err);
  } finally {
    loading.value = false;
  }

  return null;
};

async function onFirstOpen() {
  const history = await fetchConversationData();

  if (history?.length === 1) {
    activeConversationPersistence.saveState(history[0].conversation);
    activeWidget.value = ActiveWidget.Conversation;
  } else if ((history?.length ?? 0) > 1) {
    activeWidget.value = ActiveWidget.Splash;
  } else {
    onStartConversation();
  }
}

function onMinimizedClick() {
  if (previousActiveWidget.value !== undefined) {
    activeWidget.value = previousActiveWidget.value;
  } else {
    onFirstOpen();
  }
}

function setIframeState() {
  const el = document.getElementById("sa-chat-widget-iframe") as HTMLIFrameElement;
  if (el) {
    if (activeWidget.value === ActiveWidget.Minimized) {
      setMinimizedState(el, settings.value.iframeSettings);
    } else {
      setExpandedState(el, settings.value.iframeSettings);
    }
  } else {
    console.warn("Could not get element by id 'sa-chat-widget-iframe'");
  }
}

const debouncedHandleResize = debounce(setIframeState, 100);

onMounted(() => {
  setIframeState();
  window.addEventListener("resize", debouncedHandleResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", debouncedHandleResize);
});

watch(activeWidget, () => {
  activeWidgetPersistence.saveState(activeWidget.value);
  setIframeState();
});

function subscribe() {
  if (demoMode || !tenant) {
    console.debug("Demo mode. Skipping subscribe");
    return;
  }

  const contact = contactPersistence.getState();

  if (!contact) {
    console.debug("No contact. Skipping subscribe");
    return;
  }

  if (hasSubscribed.value) {
    console.debug("Already subscribed.");
    return;
  }

  useSubscribe<
    GOnWebChatConversationTimelineCreatedSubscription,
    GOnWebChatConversationTimelineCreatedSubscriptionVariables
  >(
    OnWebChatConversationTimelineCreatedDocument,
    {
      tenant: tenant,
      contact: contact,
      includeAgent:
        settings.value.conversationSettings.showAgentName || settings.value.conversationSettings.showAgentAvatar,
    },
    res => {
      if (res.onWebChatConversationTimelineCreated.data.__typename === "ConversationAgentMessage") {
        emitter.emit("agent-message-received", {
          id: res.onWebChatConversationTimelineCreated.id,
          conversation: res.onWebChatConversationTimelineCreated.conversation,
          content: getConversationTimelineContent(res.onWebChatConversationTimelineCreated.data),
          agentName: resolveDisplayName(
            res.onWebChatConversationTimelineCreated.data.agent?.firstName,
            res.onWebChatConversationTimelineCreated.data.agent?.lastName,
            settings!.value.conversationSettings.agentNameVariation
          ),
          agentInitials: resolveNameInitials(
            res.onWebChatConversationTimelineCreated.data.agent?.firstName,
            res.onWebChatConversationTimelineCreated.data.agent?.lastName
          ),
          attachments: res.onWebChatConversationTimelineCreated.data.attachments,
        });
      }

      if (res.onWebChatConversationTimelineCreated.data.__typename === "ConversationBotMessage") {
        emitter.emit("bot-message-received", {
          id: res.onWebChatConversationTimelineCreated.id,
          conversation: res.onWebChatConversationTimelineCreated.conversation,
          content: getConversationTimelineContent(res.onWebChatConversationTimelineCreated.data),
          processing: false,
        });
      }

      if (res.onWebChatConversationTimelineCreated.data.__typename === "ConversationEvent") {
        if (res.onWebChatConversationTimelineCreated.data.metadata?.__typename === "ConversationTransferredMetaData") {
          emitter.emit("event-transferred-received", {
            id: res.onWebChatConversationTimelineCreated.id,
            conversation: res.onWebChatConversationTimelineCreated.conversation,
            content: getConversationTimelineContent(res.onWebChatConversationTimelineCreated.data),
          });
        }
      }
    },
    async () => {
      // Reconnected
      console.log("Reconnecting, refetching conversation data...");
      emitter.emit("subscription-reconnected");
      await fetchConversationData();
    }
  );

  hasSubscribed.value = true;
}

onMounted(async () => {
  await checkAndPersistVisitorCountryCode();
});

useEmitterOn("go-to-conversation", async () => {
  activeWidget.value = ActiveWidget.Conversation;
});

useEmitterOn("contact-created", () => {
  subscribe();
});

useEmitterOn("error", error => {
  alert(error);
});

useEmitterOn("open-requested", () => {
  onMinimizedClick();
});

useEmitterOn("close-requested", () => {
  onClose();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (activeWidget.value === _unref(ActiveWidget).Minimized)
      ? (_openBlock(), _createBlock(MinimizedButton, {
          key: 0,
          loading: loading.value,
          onClick: onMinimizedClick
        }, null, 8, ["loading"]))
      : _createCommentVNode("", true),
    (activeWidget.value === _unref(ActiveWidget).Splash)
      ? (_openBlock(), _createBlock(SplashScreen, {
          key: 1,
          onStartConversation: onStartConversation,
          onClose: onClose,
          onClick: onConversationClick
        }))
      : _createCommentVNode("", true),
    (activeWidget.value === _unref(ActiveWidget).Conversation)
      ? (_openBlock(), _createBlock(ChatList, {
          key: 2,
          onClose: onClose,
          onBack: onBack
        }))
      : _createCommentVNode("", true),
    (activeWidget.value === _unref(ActiveWidget).PreChatSurvey)
      ? (_openBlock(), _createBlock(PreChatSurvey, {
          key: 3,
          onClose: onClose,
          onBack: onBack
        }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})